<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { Integration } from '@client/pages/manage/integrations/edit/integration';

  import { PluginsService } from '@client/services/plugins';

  import { appStore } from '@client/stores/app';

  import { deepCopy } from '@common/utils/objects';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let currentInternalStep = 0;
  let maxStepReached = 0;
  let integrations = [];
  let selectedIntegration = null;
  let plugin = null;
  let originalPlugin = null;
  let invalidRulename = false;
  let isDisabled = false;

  const loadIntegrations = async () => {
    const response = await PluginsService.getAll();
    integrations = response.map((integration) => ({
      label: integration.rulename,
      value: integration.rulename,
      ...integration
    }));
  };

  const onSelectIntegration = (event) => {
    selectedIntegration = event.detail;
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex === 0 && selectedIntegration) {
      originalPlugin = deepCopy(selectedIntegration);
      plugin = deepCopy(selectedIntegration);
      currentInternalStep = 1;
      if (currentInternalStep > maxStepReached) {
        maxStepReached = currentInternalStep;
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      const response = await PluginsService.savePlugin(plugin);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.UPDATE', { values: { item: plugin.rulename } }), 'error');
        throw new Error($format('label.FAILURE.UPDATE_INTEGRATION'));
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: plugin.rulename } }));

      step.data = {
        id: plugin.rulename,
        rollbackData: {
          originalPlugin
        }
      };

      return response;
    } catch (error) {
      notify($format('label.FAILURE.UPDATE', { values: { item: plugin.rulename } }), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    if (!step.data?.rollbackData) {
      throw new Error($format('label.NO_ROLLBACK_DATA'));
    }

    $appStore.loading = true;

    try {
      const { originalPlugin } = step.data.rollbackData;

      const response = await PluginsService.savePlugin(originalPlugin);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.ROLLBACK_INTEGRATION_UPDATE'), 'error');
        throw new Error($format('label.FAILURE.ROLLBACK_INTEGRATION_UPDATE'));
      }

      notify($format('label.SUCCESS.ROLLBACK_INTEGRATION_UPDATE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_INTEGRATION_UPDATE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const hasChanges = () => {
    if (!originalPlugin || !plugin) return false;
    return JSON.stringify(originalPlugin) !== JSON.stringify(plugin);
  };

  onMount(async () => {
    loading = true;

    try {
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to load integrations:', error);
      notify($format('label.FAILURE.LOAD_INTEGRATIONS'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      nextDisabled = true; // Always disabled on step 0 (selection step)
    } else if (currentInternalStep === 1) {
      nextDisabled = !hasChanges() || invalidRulename || !plugin?.rulename;
    } else {
      nextDisabled = true;
    }
  }
</script>

<div class="integration-update-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title="Select Integration"
    summary={selectedIntegration?.rulename || 'Not selected'}
    onNext={currentInternalStep === 0 ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={currentInternalStep === 0 && !selectedIntegration}
  >
    {#if loading}
      <div class="loading">
        <Spinner show inline />
      </div>
    {:else}
      <SectionItem label="Integration" description="Choose the integration you want to update">
        <Select
          items={integrations}
          bind:value={selectedIntegration}
          placeholder="Select an integration..."
          clearable={false}
          showChevron
          on:change={onSelectIntegration}
        />
      </SectionItem>
    {/if}
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title="Update Integration"
    summary={plugin?.rulename || 'Not configured'}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={currentInternalStep === 1 && (!hasChanges() || invalidRulename || !plugin?.rulename)}
  >
    {#if plugin}
      <Integration bind:plugin bind:invalidRulename />
    {/if}
  </Step>
</div>

<style lang="scss">
  .integration-update-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-xlarge);
    }

    :global(.section-item .row.align-items-center) {
      align-items: flex-start !important;
    }

    :global {
      .section {
        + .section {
          margin-top: var(--margin-xlarge);
        }

        .heading {
          font-weight: var(--font-weight-medium);
          margin-bottom: var(--margin-base);

          i {
            cursor: help;
            font-size: 0.8em;
          }
        }
      }
    }
  }
</style>
